(() => {
  if (!window.SKBundleIncludes) {
    window.SKBundleIncludes = {
      init(root = document) {
        if (!root || !root.querySelectorAll) {
          return;
        }

        const bundles = root.matches && root.matches('[data-bundle-includes]')
          ? [root]
          : Array.from(root.querySelectorAll('[data-bundle-includes]'));

        bundles.forEach((bundle) => {
          this.observeNativeSelectors(bundle);
          this.sync(bundle);
          bundle.dataset.bundleIncludesInitialized = 'true';
        });
      },

      observeDynamicContent() {
        if (this.dynamicContentObserver || !document.body) {
          return;
        }

        this.dynamicContentObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType !== Node.ELEMENT_NODE) {
                return;
              }

              if (node.matches('[data-bundle-includes]') || node.querySelector('[data-bundle-includes]')) {
                this.init(node);
              }
            });
          });
        });

        this.dynamicContentObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });

        // Quick-buy drawer/popover content is fetched and injected after open.
        // openable-element:load:end does not bubble, so listen in capture phase.
        document.addEventListener(
          'openable-element:load:end',
          (event) => {
            if (event.target && event.target.querySelector) {
              this.init(event.target);
            }
          },
          true
        );
      },

      normalize(value) {
        return (value || '').replace(/:\s*$/, '').trim().toLowerCase();
      },

      getOptionName(control) {
        const selector = control.closest('.product-form__option-selector');
        const label = selector ? selector.querySelector('.product-form__option-name') : null;

        return this.normalize(label ? label.textContent : '');
      },

      getControlValue(control) {
        if (control.tagName === 'SELECT') {
          return control.value;
        }

        return control.value;
      },

      getNativeControls(variants) {
        return Array.from(variants.querySelectorAll('input[name^="option"], select[name^="option"]'));
      },

      getSelectedOptions(variants) {
        return this.getNativeControls(variants)
          .filter((control) => control.tagName === 'SELECT' || control.checked)
          .map((control) => ({
            name: this.getOptionName(control),
            position: control.name.replace(/^option/, ''),
            value: this.getControlValue(control),
          }));
      },

      findNativeControl(variants, optionName, optionValue, optionPosition) {
        const normalizedName = this.normalize(optionName);
        const controls = this.getNativeControls(variants).filter((control) => {
          if (optionPosition && control.name !== `option${optionPosition}`) {
            return false;
          }

          if (control.tagName === 'SELECT') {
            return Array.from(control.options).some((option) => option.value === optionValue);
          }

          return control.value === optionValue;
        });

        const exactControl = controls.find((control) => this.getOptionName(control) === normalizedName);

        if (exactControl) {
          return exactControl;
        }

        if (optionPosition || controls.length === 1) {
          return controls[0] || null;
        }

        return null;
      },

      getNativeAvailability(variants, button) {
        const fallbackAvailability = button.dataset.bundleHasAvailableVariant !== 'false';
        const initialAvailability = button.dataset.bundleInitiallyAvailable !== 'false';
        const control = this.findNativeControl(
          variants,
          button.dataset.bundleOptionName,
          button.dataset.bundleOptionValue,
          button.dataset.bundleOptionPosition
        );

        if (!variants.product) {
          return initialAvailability;
        }

        if (!control) {
          return fallbackAvailability;
        }

        let nativeOption = null;

        if (control.tagName === 'SELECT') {
          const selector = control.closest('.product-form__option-selector');
          const optionIndex = Array.from(control.options).findIndex((option) => {
            return option.value === button.dataset.bundleOptionValue;
          });

          nativeOption = selector
            ? selector.querySelectorAll('.combo-box__option-item')[optionIndex]
            : null;
        } else {
          nativeOption = control.closest('.color-swatch, .variant-swatch, .block-swatch');
        }

        if (!nativeOption) {
          return fallbackAvailability;
        }

        return fallbackAvailability && !nativeOption.hidden && !nativeOption.classList.contains('is-disabled');
      },

      syncAvailability(bundle, variants) {
        bundle.querySelectorAll('[data-bundle-option-value]').forEach((button) => {
          const available = this.getNativeAvailability(variants, button);

          button.toggleAttribute('disabled', !available);
          button.classList.toggle('is-disabled', !available);
          button.setAttribute('aria-disabled', available ? 'false' : 'true');
        });

        bundle.querySelectorAll('[data-bundle-dropdown]').forEach((dropdown) => {
          const options = Array.from(dropdown.querySelectorAll('[data-bundle-dropdown-option]'));
          const hasAvailableOption = options.some((option) => !option.disabled);
          const toggle = dropdown.querySelector('[data-bundle-dropdown-toggle]');

          if (toggle) {
            toggle.disabled = !hasAvailableOption;
          }
        });
      },

      closeDropdown(dropdown) {
        if (!dropdown) {
          return;
        }

        dropdown.classList.remove('is-open');

        const toggle = dropdown.querySelector('[data-bundle-dropdown-toggle]');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
        }
      },

      closeOtherDropdowns(activeDropdown = null) {
        document.querySelectorAll('[data-bundle-dropdown].is-open').forEach((dropdown) => {
          if (dropdown !== activeDropdown) {
            this.closeDropdown(dropdown);
          }
        });
      },

      toggleDropdown(toggle) {
        const dropdown = toggle.closest('[data-bundle-dropdown]');

        if (!dropdown || toggle.disabled) {
          return;
        }

        const shouldOpen = !dropdown.classList.contains('is-open');
        this.closeOtherDropdowns(dropdown);
        dropdown.classList.toggle('is-open', shouldOpen);
        toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      },

      observeNativeSelectors(bundle) {
        if (bundle.skBundleInventoryObserver) {
          return;
        }

        const variants = bundle.closest('product-variants');

        if (!variants) {
          return;
        }

        const observer = new MutationObserver(() => this.sync(bundle));

        variants.querySelectorAll('.product-form__option-selector').forEach((selector) => {
          observer.observe(selector, {
            attributes: true,
            attributeFilter: ['class', 'hidden'],
            subtree: true,
          });
        });

        bundle.skBundleInventoryObserver = observer;
      },

      select(button) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true' || button.hasAttribute('data-bundle-static')) {
          return;
        }

        const bundle = button.closest('[data-bundle-includes]');
        const variants = button.closest('product-variants');

        if (!bundle || !variants) {
          return;
        }

        const control = this.findNativeControl(
          variants,
          button.dataset.bundleOptionName,
          button.dataset.bundleOptionValue,
          button.dataset.bundleOptionPosition
        );

        if (!control) {
          return;
        }

        this.isSelectingBundleOption = true;

        if (control.tagName === 'SELECT') {
          if (control.value !== button.dataset.bundleOptionValue) {
            control.value = button.dataset.bundleOptionValue;
            control.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (!control.checked) {
          control.checked = true;
          control.dispatchEvent(new Event('change', { bubbles: true }));
        }

        window.requestAnimationFrame(() => {
          this.isSelectingBundleOption = false;
        });

        this.updateCardImage(button);

        if (button.hasAttribute('data-bundle-dropdown-option')) {
          this.closeDropdown(button.closest('[data-bundle-dropdown]'));
        }

        window.requestAnimationFrame(() => this.sync(bundle));
      },

      updateCardImage(button) {
        const imageSrc = button.dataset.bundleImageSrc;

        if (!imageSrc) {
          return;
        }

        const card = button.closest('[data-bundle-item-card]');
        const image = card ? card.querySelector('[data-bundle-item-image]') : null;

        if (!image) {
          return;
        }

        image.src = imageSrc;
        image.removeAttribute('srcset');
      },

      sync(bundle) {
        const variants = bundle.closest('product-variants');

        if (!variants) {
          return;
        }

        this.syncAvailability(bundle, variants);

        const selectedOptions = this.getSelectedOptions(variants);

        bundle.querySelectorAll('[data-bundle-option-row]').forEach((row) => {
          const buttons = Array.from(row.querySelectorAll('[data-bundle-option-value]'));
          const dropdown = row.querySelector('[data-bundle-dropdown]');
          const rowOptionName = this.normalize(row.dataset.bundleOptionName);
          let activeButton = buttons.find((button) => {
            return selectedOptions.some((selectedOption) => {
              if (button.dataset.bundleOptionPosition) {
                return selectedOption.position === button.dataset.bundleOptionPosition
                  && selectedOption.value === button.dataset.bundleOptionValue;
              }

              return selectedOption.name === rowOptionName && selectedOption.value === button.dataset.bundleOptionValue;
            });
          });

          if (!activeButton && buttons.length === 1) {
            activeButton = buttons[0];
          }

          buttons.forEach((button) => {
            if (button.hasAttribute('data-bundle-dropdown-option')) {
              button.setAttribute('aria-selected', button === activeButton ? 'true' : 'false');
              button.classList.toggle('is-selected', button === activeButton);
              return;
            }

            button.setAttribute('aria-pressed', button === activeButton ? 'true' : 'false');
          });

          if (dropdown && activeButton) {
            const selectedLabel = dropdown.querySelector('[data-bundle-dropdown-selected]');
            const selectedSwatch = dropdown.querySelector('[data-bundle-selected-swatch]');

            if (selectedLabel) {
              selectedLabel.textContent = activeButton.dataset.bundleOptionValue;
            }

            if (selectedSwatch && activeButton.dataset.bundleOptionColorStyle) {
              selectedSwatch.setAttribute('style', activeButton.dataset.bundleOptionColorStyle);
              selectedSwatch.classList.toggle('sk-bundle-dropdown__swatch--white', activeButton.dataset.bundleOptionColorIsWhite === 'true');
            }
          }

          if (activeButton) {
            this.updateCardImage(activeButton);
          }

          const valueLabel = row.querySelector('[data-bundle-selected-value]');
          if (valueLabel && activeButton) {
            valueLabel.textContent = activeButton.dataset.bundleOptionValue;
          }
        });
      },
    };

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-bundle-dropdown-toggle]');

      if (toggle) {
        window.SKBundleIncludes.toggleDropdown(toggle);
        return;
      }

      const button = event.target.closest('[data-bundle-option-value]');

      if (button) {
        window.SKBundleIncludes.select(button);
        return;
      }

      if (!event.target.closest('[data-bundle-dropdown]')) {
        window.SKBundleIncludes.closeOtherDropdowns();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        window.SKBundleIncludes.closeOtherDropdowns();
      }
    });

    document.addEventListener('variant:changed', (event) => {
      const form = event.target;

      if (!(form instanceof Element)) {
        return;
      }

      const formId = form.id;
      let bundles = formId
        ? Array.from(document.querySelectorAll(`[data-bundle-includes][data-form-id="${formId}"]`))
        : [];

      // Quick-buy may remap form ids; fall back to the nearest loaded drawer/popover.
      if (!bundles.length) {
        const root = form.closest('quick-buy-drawer, quick-buy-popover, .product-form') || form;
        bundles = Array.from(root.querySelectorAll('[data-bundle-includes]'));
      }

      bundles.forEach((bundle) => {
        window.SKBundleIncludes.sync(bundle);
      });
    });

    document.addEventListener('shopify:section:load', (event) => {
      window.SKBundleIncludes.init(event.target);
    });
  }

  const initBundleIncludes = () => {
    window.SKBundleIncludes.init(document);
    window.SKBundleIncludes.observeDynamicContent();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleIncludes, { once: true });
  } else {
    initBundleIncludes();
  }
})();
